import React, { useState, useCallback } from 'react';
import { X, Upload, Loader, FileText, Check } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

export default function AIInvoiceUploadModal({ show, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const r = await api.post('/api/expenses/upload-invoice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Backend returns { ok, extracted_data, confidence, message }
      setExtractedData(r.data.extracted_data || r.data);
      toast.success(r.data.message || 'Invoice data extracted!');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to extract invoice data');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = () => {
    if (extractedData) {
      onSuccess(extractedData);
      reset();
      onClose();
    }
  };

  const reset = () => {
    setFile(null);
    setExtractedData(null);
    setUploading(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="text-purple-400" size={24} />
            AI Invoice Recognition
          </h2>
          <button onClick={() => { reset(); onClose(); }} className="text-zinc-400 hover:text-white transition">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {!extractedData ? (
            <>
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-12 transition ${
                  dragActive
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-white/20 bg-zinc-800/50'
                }`}>
                <div className="text-center">
                  <Upload className="mx-auto mb-4 text-zinc-400" size={48} />
                  <p className="text-white font-semibold mb-2">
                    Drag & drop invoice here
                  </p>
                  <p className="text-sm text-zinc-400 mb-4">
                    or click to browse (PDF, JPG, PNG)
                  </p>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    id="invoice-upload"
                  />
                  <label
                    htmlFor="invoice-upload"
                    className="inline-block px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg cursor-pointer transition">
                    Browse Files
                  </label>
                </div>
              </div>

              {file && (
                <div className="bg-zinc-800/50 border border-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="text-purple-400" size={20} />
                      <div>
                        <p className="text-white font-semibold">{file.name}</p>
                        <p className="text-xs text-zinc-400">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setFile(null)}
                      className="text-zinc-400 hover:text-white transition">
                      <X size={18} />
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition flex items-center justify-center gap-2">
                {uploading ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    Extracting Data...
                  </>
                ) : (
                  <>
                    <Upload size={20} />
                    Upload & Extract
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-green-400 text-sm flex items-center gap-2">
                <Check size={18} />
                Invoice data extracted! Review and confirm below.
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">
                      Vendor
                    </label>
                    <input
                      type="text"
                      value={extractedData.vendor || ''}
                      readOnly
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">
                      Amount
                    </label>
                    <input
                      type="text"
                      value={`${extractedData.currency || 'USD'} ${extractedData.amount || '0'}`}
                      readOnly
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">
                      Date
                    </label>
                    <input
                      type="text"
                      value={extractedData.date || ''}
                      readOnly
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">
                      Category
                    </label>
                    <input
                      type="text"
                      value={extractedData.category || ''}
                      readOnly
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                </div>

                {extractedData.description && (
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={extractedData.description}
                      readOnly
                      rows={3}
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleConfirm}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg transition">
                  Confirm & Add Expense
                </button>
                <button
                  onClick={reset}
                  className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white font-bold py-3 rounded-lg transition">
                  Upload Another
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
